import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Filter, Search } from 'lucide-react';
import { Badge, Button, Card, CardBody, EmptyState, Select } from '@solara/ui-shared';
import { useI18n, useTranslation } from '@solara/i18n';
import { useInvoices } from '@solara/api-client/react';
import type { SampleInvoice } from '@solara/sample-data';
import { adaptInvoices } from '../data/adapters';
import { LoadingBlock, ErrorBanner } from '../components/QueryState';

type StatusFilter = 'all' | 'AP' | 'AB' | 'RE' | 'PENDING';

export default function InvoicesPage() {
  const { t: tNav } = useTranslation('common');
  const { t } = useTranslation('invoice');
  const { locale } = useI18n();
  const aedFmt = new Intl.NumberFormat(locale, { style: 'currency', currency: 'AED' });

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');

  const invoicesQuery = useInvoices({ search: query || undefined });
  const allInvoices = useMemo(
    () => adaptInvoices(invoicesQuery.data),
    [invoicesQuery.data],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return allInvoices.filter((inv) => {
      if (status !== 'all' && inv.mlsStatus !== status) return false;
      if (!q) return true;
      return (
        inv.invoiceNumber.toLowerCase().includes(q) ||
        inv.buyerName.toLowerCase().includes(q) ||
        inv.buyerTrn.includes(q)
      );
    });
  }, [allInvoices, query, status]);

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{tNav('nav.invoices')}</h1>
          <p className="text-fg-muted text-sm">
            {t('list.subtitle', { count: filtered.length, total: allInvoices.length })}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 sm:flex-initial">
            <Search className="absolute inset-y-0 start-3 my-auto h-4 w-4 text-fg-subtle" />
            <input
              type="search"
              placeholder={t('list.search')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full sm:w-64 h-11 ps-9 pe-3 rounded-lg bg-surface border border-border text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30"
              aria-label={t('list.search')}
            />
          </div>
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as StatusFilter)}
            options={[
              { value: 'all', label: t('list.filter.all') },
              { value: 'AP', label: t('list.filter.ap') },
              { value: 'AB', label: t('list.filter.ab') },
              { value: 'RE', label: t('list.filter.re') },
              { value: 'PENDING', label: t('list.filter.pending') },
            ]}
            containerClassName="w-44"
          />
          <Button variant="outline" size="md" className="gap-2">
            <Filter className="h-4 w-4" />
            <span className="hidden sm:inline">{t('list.more')}</span>
          </Button>
        </div>
      </header>

      {invoicesQuery.isLoading ? (
        <LoadingBlock label={t('list.loading', { defaultValue: 'Loading invoices…' })} />
      ) : invoicesQuery.error ? (
        <ErrorBanner error={invoicesQuery.error} />
      ) : filtered.length === 0 ? (
        <EmptyState icon={<Search className="h-6 w-6" />} title={t('list.empty')} />
      ) : (
        <div className="grid gap-3">
          {filtered.map((inv) => (
            <InvoiceRow key={inv.invoiceId} inv={inv} aedFmt={aedFmt} t={t} />
          ))}
        </div>
      )}
    </div>
  );
}

function InvoiceRow({
  inv,
  aedFmt,
  t,
}: {
  inv: SampleInvoice;
  aedFmt: Intl.NumberFormat;
  t: (k: string, vars?: Record<string, unknown>) => string;
}) {
  const { locale } = useI18n();
  const dateFmt = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' });
  const buyerLabel = locale === 'ar-AE' ? inv.buyerNameAr : inv.buyerName;
  const vatTotal = inv.totalVatAmount;

  return (
    <Link to={`/invoices/${inv.invoiceId}`} className="block group">
      <Card className="rounded-2xl transition-shadow group-hover:shadow-md group-focus-visible:ring-2 group-focus-visible:ring-brand-500/40">
        <CardBody className="p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold">{inv.invoiceNumber}</span>
              <StatusPill status={inv.mlsStatus} t={t} />
              <Badge tone="neutral" className="font-mono text-[10px]">
                {t('list.row.items', { count: inv.lineItems.length })}
              </Badge>
            </div>
            <span className="text-sm text-fg-muted truncate" dir="auto">
              {buyerLabel}
            </span>
            <span className="text-xs text-fg-subtle num">
              {dateFmt.format(new Date(inv.issueDate))} · {t('list.row.trn', { trn: inv.buyerTrn })}
            </span>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="text-end">
              <div className="text-lg font-semibold num">{aedFmt.format(inv.totalGrossAmount)}</div>
              <div className="text-xs text-fg-subtle num">
                {t('list.row.net', { net: aedFmt.format(inv.totalNetAmount) })} ·{' '}
                {t('list.row.vat', { vat: aedFmt.format(vatTotal) })}
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-fg-subtle group-hover:text-brand-700 rtl-mirror" />
          </div>
        </CardBody>
      </Card>
    </Link>
  );
}

function StatusPill({
  status,
  t,
}: {
  status: 'AP' | 'AB' | 'RE' | 'PENDING';
  t: (k: string, vars?: Record<string, unknown>) => string;
}) {
  const map = {
    AP: { tone: 'success' as const, key: 'list.filter.ap' },
    AB: { tone: 'info' as const, key: 'list.filter.ab' },
    RE: { tone: 'danger' as const, key: 'list.filter.re' },
    PENDING: { tone: 'neutral' as const, key: 'list.filter.pending' },
  };
  const cfg = map[status];
  return <Badge tone={cfg.tone}>{t(cfg.key)}</Badge>;
}
